import { cn } from '@/lib/helpers/utils';
import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionForceMount,
  AccordionForceMountItem,
  AccordionItem,
  AccordionTrigger
} from '../ui/Accordion';
import SectionOneCol from './sections/SectionOneCol';

type FAQ = {
  question: any;
  answer: any;
};

interface AccordionBlockProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'content' | 'title'> {
  title?: string | object;
  content: FAQ[];
  indent?: boolean;
  type?: 'single' | 'multiple';
  containerClasses?: string;
  accordionItemClasses?: string;
  questionClasses?: string;
  answerClasses?: string;
  forceMount?: boolean | undefined;
}

/**
 * Accordion component
 * @return {JSX}
 */
const AccordionBlock = ({
  title,
  content,
  indent,
  containerClasses,
  accordionItemClasses,
  questionClasses,
  answerClasses,
  type = 'single',
  forceMount = false,
  ...props
}: AccordionBlockProps) => {
  const [openItems, setOpenItems] = React.useState<Set<number>>(new Set());

  if (content.length === 1 && !content[0].question && !content[0].answer) {
    return null;
  }

  const handleToggle = (index: number) => {
    if (type === 'single') {
      setOpenItems(new Set(openItems.has(index) ? [] : [index]));
    } else {
      const newOpenItems = new Set(openItems);
      if (newOpenItems.has(index)) {
        newOpenItems.delete(index);
      } else {
        newOpenItems.add(index);
      }
      setOpenItems(newOpenItems);
    }
  };

  return (
    <SectionOneCol
      title={title ? title : null}
      indent={indent}
      paddingClassName={containerClasses}
      content={
        content && (
          <div
            className={cn('section-container-lg suppress-section-container-padding flex flex-col w-full')}
            {...props}
          >
            {forceMount ? (
              <AccordionForceMount type={type} className="space-y-5">
                {content.map((panel: FAQ, i: number) => {
                  const htmlRemoveRegex = /(<([^>]+)>)/gi;
                  return (
                    panel?.question &&
                    panel?.answer && (
                      <AccordionForceMountItem
                        key={`panel-item-${i}`}
                        isOpen={openItems.has(i)}
                        onToggle={() => handleToggle(i)}
                        className={cn(
                          'group bg-gray-50 shadow-sm rounded-lg p-6 hover:text-primary',
                          accordionItemClasses
                        )}
                        title={
                          <div
                            className={cn('font-medium text-left text-xl pr-4', questionClasses)}
                            dangerouslySetInnerHTML={{ __html: panel?.question?.replace(htmlRemoveRegex, '') }}
                          />
                        }
                      >
                        {typeof panel?.answer === 'object' ? (
                          panel?.answer
                        ) : (
                          <p
                            className={cn('pt-4 text-gray-700 leading-7', answerClasses)}
                            dangerouslySetInnerHTML={{ __html: [panel?.answer] }}
                          />
                        )}
                      </AccordionForceMountItem>
                    )
                  );
                })}
              </AccordionForceMount>
            ) : (
              <Accordion type={type} className="space-y-5">
                {content.map((panel: FAQ, i: number) => {
                  const htmlRemoveRegex = /(<([^>]+)>)/gi;
                  return (
                    panel?.question &&
                    panel?.answer && (
                      <AccordionItem
                        key={`panel-item-${i}`}
                        value={`panel-item-${i}`}
                        className={cn(
                          'group bg-gray-50 shadow-sm rounded-lg p-6 hover:text-primary',
                          accordionItemClasses
                        )}
                      >
                        <AccordionTrigger>
                          <div
                            className={cn('font-medium text-left text-xl pr-4', questionClasses)}
                            dangerouslySetInnerHTML={{ __html: panel?.question?.replace(htmlRemoveRegex, '') }}
                          />
                        </AccordionTrigger>
                        <AccordionContent>
                          {typeof panel?.answer === 'object' ? (
                            panel?.answer
                          ) : (
                            <p
                              className={cn('pt-4 text-gray-700 leading-7', answerClasses)}
                              dangerouslySetInnerHTML={{ __html: [panel?.answer] }}
                            />
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    )
                  );
                })}
              </Accordion>
            )}
          </div>
        )
      }
    />
  );
};

export default AccordionBlock;
